import PropTypes from "prop-types";
import ArrowEditor from "../arrow/ArrowEditor";
import CircleEditor from "../circle/CircleEditor";
import ImageEditor from "../image/ImageEditor";
import RectangleEditor from "../rectangle/RectangleEditor";
import TextEditor from "../text-editor/TextEditor";
import { isShapeType } from "../../../../util/shapeTypeMapper";

import "../styles.css";
import LineEditor from "../line/LineEditor";
import UnifiedLineArrowEditor from "../unified/UnifiedLineArrowEditor";
import SelectionEditor from "../selection/SelectionEditor";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";

function ShapeEditor({
  mainCanvasWidthPx,
  selectedObject,
  updateShapeProperties,
  updateArrowProperties,
  updateTextProperties,
  setSelectedObject,
  setSelectedObjects = null,
  selectedObjects = [],
  groupDragDisplay = null,
  selectedText,
  deleteSelectedObject,
  onAddText,
  onRemoveText,
  toggleImageCompression,
  copySelectedImage,
  cutSelectedImage,
  transformerRef,
  updateImageProperties,
  canvasHeight,
  updateDoubleSidedArrowProperties,
  setImages,
  updateLineProperties,
  updateVideoProperties,
  setVideos,
  isCropModalOpen,
  setIsCropModalOpen,
  setCropModalOpenRefOnly = () => {},
  setCropModalRootRef = () => {},
  setIsImageLoading,
  setImageLoadingProgress,
  saveToHistory,
  latestUpdatedImagesRef = null,
  latestImageEditorRefUrlRef = null,
  setPendingChanges = null,
  pendingChangesRef = null,
  isCloneMode = false,
  onToggleCloneMode,
  lockedAction = null,
  onStopDuplicatingOrUnlockTool,
  isMultiSelectMode = false,
  setIsMultiSelectMode = null,
  individuallySelectedIds = new Set(),
  setIndividuallySelectedIds = null,
  handleCopy = null,
  handleCut = null,
  handleCopyGroupBetweenCanvases = null, // Handler to copy/move entire group between canvases
  // Element arrays for cross-canvas selection
  rectangles = [],
  circles = [],
  images = [],
  texts = [],
  arrows = [],
  lines = [],
  doubleSidedArrows = [],
  offCanvasRectangles = [],
  offCanvasCircles = [],
  offCanvasImages = [],
  offCanvasTexts = [],
  offCanvasArrows = [],
  offCanvasLines = [],
  offCanvasDoubleSidedArrows = [],
  // Setter functions for deleting elements
  setRectangles = null,
  setCircles = null,
  setTexts = null,
  setArrows = null,
  setLines = null,
  setDoubleSidedArrows = null,
  setOffCanvasRectangles = null,
  setOffCanvasCircles = null,
  setOffCanvasImages = null,
  setOffCanvasTexts = null,
  setOffCanvasArrows = null,
  setOffCanvasLines = null,
  setOffCanvasDoubleSidedArrows = null,
  // Removed state setters for tracking deleted elements
  setRemovedRectangles = null,
  setRemovedCircles = null,
  setRemovedArrows = null,
  setRemovedDoubleSidedArrows = null,
  setRemovedScribbles = null,
  setRemovedTexts = null,
  setRemovedImages = null,
  setRemovedVideos = null,
  setRemovedLines = null,
  setRemovedOffCanvasRectangles = null,
  setRemovedOffCanvasCircles = null,
  setRemovedOffCanvasImages = null,
  setRemovedOffCanvasTexts = null,
  setRemovedOffCanvasArrows = null,
  setRemovedOffCanvasLines = null,
  setRemovedOffCanvasDoubleSidedArrows = null,
}) {
  const [collapsed, setCollapsed] = useState(false);

  // Custom scrollbar refs and state (adapted from MyCanvas)
  const containerRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const vTrackRef = useRef(null);
  const isUserScrollingRef = useRef(false);
  const [scrollMetrics, setScrollMetrics] = useState({
    scrollTop: 0,
    scrollHeight: 1,
    clientHeight: 1,
  });
  // Throttle scroll updates for better performance
  const updateScrollMetrics = useCallback(() => {
    const el = scrollAreaRef.current;
    if (!el) return;

    const newMetrics = {
      scrollTop: el.scrollTop,
      scrollHeight: el.scrollHeight || 1,
      clientHeight: el.clientHeight || 1,
    };

    // Avoid unnecessary state updates/re-renders to prevent scroll "bouncing"
    setScrollMetrics((prev) => {
      if (
        prev.scrollTop === newMetrics.scrollTop &&
        prev.scrollHeight === newMetrics.scrollHeight &&
        prev.clientHeight === newMetrics.clientHeight
      ) {
        return prev;
      }
      return newMetrics;
    });
  }, []);

  // Throttled version for scroll events
  const throttledUpdateScrollMetrics = useCallback(() => {
    requestAnimationFrame(updateScrollMetrics);
  }, [updateScrollMetrics]);

  // Prevent scroll "chain" to the underlying canvas/body when at the top/bottom
  // of the properties panel. This is especially important for large editors
  // like ImageEditor, where the wheel events can otherwise bubble and cause
  // selection/viewport changes that reset the panel scroll position.
  const handleScrollWheel = useCallback((e) => {
    const el = scrollAreaRef.current;
    if (!el) return;

    const deltaY = e.deltaY;
    const scrollableHeight = Math.max(0, el.scrollHeight - el.clientHeight);
    if (scrollableHeight <= 0) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }

    // Always consume wheel events from the panel so momentum scrolling cannot
    // chain into parent containers (canvas/body) at boundaries.
    e.stopPropagation();
    e.preventDefault();

    const nextScrollTop = Math.max(
      0,
      Math.min(scrollableHeight, el.scrollTop + deltaY)
    );
    if (nextScrollTop !== el.scrollTop) {
      el.scrollTop = nextScrollTop;
    }
  }, []);

  useEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) return;
    updateScrollMetrics();
    let scrollEndTimeout = null;
    const onScroll = () => {
      // Mark that the user is actively scrolling; this helps us avoid
      // fighting with ResizeObserver-driven updates that can cause jumps.
      isUserScrollingRef.current = true;
      if (scrollEndTimeout) {
        clearTimeout(scrollEndTimeout);
      }
      scrollEndTimeout = setTimeout(() => {
        isUserScrollingRef.current = false;
      }, 150);

      throttledUpdateScrollMetrics();
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", updateScrollMetrics);
    return () => {
      if (scrollEndTimeout) {
        clearTimeout(scrollEndTimeout);
      }
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateScrollMetrics);
    };
  }, [updateScrollMetrics, throttledUpdateScrollMetrics]);

  // Update scroll metrics when selectedObject changes (content might change)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      updateScrollMetrics();
    }, 100); // Small delay to ensure DOM has updated

    return () => clearTimeout(timeoutId);
  }, [selectedObject, collapsed, updateScrollMetrics]);

  // Derived sizes/positions for custom thumb
  const vTrackSize = vTrackRef.current
    ? vTrackRef.current.clientHeight
    : scrollMetrics.clientHeight;

  const verticalVisibleRatio = Math.min(
    1,
    scrollMetrics.clientHeight / Math.max(1, scrollMetrics.scrollHeight)
  );

  // More responsive thumb sizing with better minimum size
  const vThumbHeight = useMemo(() => {
    const minSize = Math.max(16, Math.floor(vTrackSize * 0.05)); // 5% of track size or 16px minimum
    const calculatedSize = Math.floor(verticalVisibleRatio * vTrackSize);
    return Math.max(minSize, calculatedSize);
  }, [verticalVisibleRatio, vTrackSize]);

  const vScrollable = Math.max(
    0,
    scrollMetrics.scrollHeight - scrollMetrics.clientHeight
  );

  const vMaxThumbOffset = Math.max(0, vTrackSize - vThumbHeight);

  // More precise thumb positioning calculations
  const vThumbTop = useMemo(() => {
    if (vScrollable <= 0 || vMaxThumbOffset <= 0) return 0;
    const ratio = Math.max(
      0,
      Math.min(1, scrollMetrics.scrollTop / vScrollable)
    );
    return Math.round(ratio * vMaxThumbOffset);
  }, [scrollMetrics.scrollTop, vScrollable, vMaxThumbOffset]);

  // Drag handling for thumb
  const [dragState, setDragState] = useState(null);

  useEffect(() => {
    const onCanvasEscape = () => setDragState(null);
    window.addEventListener("canvasEscape", onCanvasEscape);
    return () => window.removeEventListener("canvasEscape", onCanvasEscape);
  }, []);

  const handleVerticalThumbMouseDown = (e) => {
    e.preventDefault();
    const el = scrollAreaRef.current;
    if (!el) return;

    setDragState({
      axis: "v",
      startClient: e.clientY,
      startScroll: el.scrollTop,
    });
  };

  const handleVerticalTrackMouseDown = (e) => {
    // Click on track to jump toward that position
    if (vTrackRef.current && e.target === vTrackRef.current) {
      const el = scrollAreaRef.current;
      if (!el) return;

      const rect = vTrackRef.current.getBoundingClientRect();
      const contentScrollable = Math.max(0, el.scrollHeight - el.clientHeight);
      if (contentScrollable <= 0) return;

      // Simple proportional click with smooth scrolling
      const clickY = e.clientY - rect.top;
      const scrollRatio = Math.max(0, Math.min(1, clickY / rect.height));
      const newScrollTop = scrollRatio * contentScrollable;

      el.scrollTo({
        top: newScrollTop,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    if (!dragState) return;

    const onMove = (e) => {
      const el = scrollAreaRef.current;
      if (!el) return;

      if (dragState.axis === "v") {
        // Less sensitive vertical scroll
        const delta = e.clientY - dragState.startClient;
        const contentScrollable = Math.max(
          0,
          el.scrollHeight - el.clientHeight
        );

        if (contentScrollable <= 0) return;

        // Use multiplier for better sensitivity
        const multiplier = Math.max(0.5, 0.8);
        const newScrollTop = dragState.startScroll + delta * multiplier;
        const clampedScrollTop = Math.max(
          0,
          Math.min(contentScrollable, newScrollTop)
        );

        // Use requestAnimationFrame for smoother scrolling
        requestAnimationFrame(() => {
          el.scrollTop = clampedScrollTop;
        });
      }
    };

    const onUp = () => {
      setDragState(null);
    };

    document.addEventListener("mousemove", onMove, { passive: false });
    document.addEventListener("mouseup", onUp);

    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [dragState]);

  // Stop clicks/mousedown inside the panel from bubbling to document so any stage/document listener doesn't clear selection (fixes SelectionEditor overlap on narrow screens)
  useEffect(() => {
    const handleBubble = (e) => {
      const container = containerRef.current;
      if (container && container.contains(e.target)) {
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };

    document.addEventListener("click", handleBubble, false);
    document.addEventListener("mousedown", handleBubble, false);

    return () => {
      document.removeEventListener("click", handleBubble, false);
      document.removeEventListener("mousedown", handleBubble, false);
    };
  }, []);

  // Check if we're in multi-selection mode (2+ elements)
  const safeSelectedObjects = selectedObjects || [];
  const isMultiSelection = safeSelectedObjects.length > 1;
  // Show editor if we have selectedObjects OR selectedObject
  const hasSelection = safeSelectedObjects.length > 0 || selectedObject;

  // Ensure selectedObject is set if we have selectedObjects but no selectedObject
  useEffect(() => {
    if (
      !selectedObject &&
      safeSelectedObjects.length > 0 &&
      setSelectedObject
    ) {
      setSelectedObject(safeSelectedObjects[0]);
    }
  }, [safeSelectedObjects, selectedObject, setSelectedObject]);

  // Get the effective selected object (use selectedObject if available, otherwise first from selectedObjects)
  const effectiveSelectedObject =
    selectedObject ||
    (safeSelectedObjects.length > 0 ? safeSelectedObjects[0] : null);
  const effectiveSelectedObjectWithCanvasHint = useMemo(() => {
    if (!effectiveSelectedObject?.id) return effectiveSelectedObject;
    const id = effectiveSelectedObject.id;
    const inOffCanvas =
      offCanvasRectangles?.some((item) => item?.id === id) ||
      offCanvasCircles?.some((item) => item?.id === id) ||
      offCanvasImages?.some((item) => item?.id === id) ||
      offCanvasTexts?.some((item) => item?.id === id) ||
      offCanvasArrows?.some((item) => item?.id === id) ||
      offCanvasLines?.some((item) => item?.id === id) ||
      offCanvasDoubleSidedArrows?.some((item) => item?.id === id);
    if (!inOffCanvas) return effectiveSelectedObject;
    return {
      ...effectiveSelectedObject,
      isOffCanvas: true,
      _isInOffCanvasDuringDrag:
        effectiveSelectedObject?._isInOffCanvasDuringDrag ?? true,
      _previewTargetStage:
        effectiveSelectedObject?._previewTargetStage || "offCanvas",
    };
  }, [
    effectiveSelectedObject,
    offCanvasRectangles,
    offCanvasCircles,
    offCanvasImages,
    offCanvasTexts,
    offCanvasArrows,
    offCanvasLines,
    offCanvasDoubleSidedArrows,
  ]);

  // Helper function to format display name for strokes (lines, arrows, double-sided arrows)
  const getDisplayName = (obj) => {
    if (!obj) return null;

    // Check if it's a line, arrow, or double-sided arrow
    const isStrokeType =
      isShapeType(obj.type, "Line") ||
      isShapeType(obj.type, "Arrow") ||
      isShapeType(obj.type, "DoubleSidedArrow");

    if (isStrokeType) {
      // Extract number from name (e.g., "Line 1" -> "1", "Arrow 2" -> "2", "Double Sided Arrow 3" -> "3")
      const name = obj.name || "";
      const match = name.match(/\d+$/); // Match number at the end
      const number = match ? match[0] : "1";
      return `Stroke ${number}`;
    }

    // For other types, return the original name, id, or type
    return obj.name || obj.id || obj.type;
  };

  const shouldRenderShapePanel =
    selectedObject?.type !== "Text" || isMultiSelectMode || isMultiSelection;

  const hasRenderableShapeContent = Boolean(
    hasSelection && effectiveSelectedObject && shouldRenderShapePanel,
  );

  const showStopDuplicatingPanel =
    typeof onStopDuplicatingOrUnlockTool === "function" &&
    (isCloneMode || lockedAction != null);

  if (!showStopDuplicatingPanel && !hasRenderableShapeContent) {
    return null;
  }

  return (
    <div
    ref={containerRef}
    data-canvas-chrome
    data-block-leave-canvas
    className={`shape-properties-panel shape-editor-container${
      collapsed ? " collapsed" : ""
    }`}
    style={{ pointerEvents: "auto" }}
    onClick={(e) => {
      // Stop propagation to prevent parent handlers from clearing selectedObject
      e.stopPropagation();
    }}
    onMouseDown={(e) => {
      // Stop propagation on mousedown as well
      e.stopPropagation();
    }}
  >
    <div
      style={{
        position: "relative",
        height: "100%",
        overflow: "hidden",
        pointerEvents: "auto",
      }}
    >
      <div
        ref={scrollAreaRef}
        className="scrollable-content shape-editor-scrollable"
        style={{
          height: "100%",
          overflowY: "auto",
          overflowX: "hidden",
          overscrollBehavior: "contain",
          paddingRight: "16px", // No padding needed for Arrow/Line
          paddingBottom: "20px", // No bottom padding for Arrow/Line
          pointerEvents: "auto",
          /* Hide native scrollbars */
          scrollbarWidth: "none" /* Firefox */,
          msOverflowStyle: "none" /* IE/Edge */,
        }}
        onWheel={handleScrollWheel}
      >
        {/* Content wrapper to ensure proper spacing and accessibility */}
        <div
          style={{
            minHeight: "100%",
            paddingBottom: showStopDuplicatingPanel ? 0 : "40px",
            pointerEvents: "auto",
            display: "flex",
            flexDirection: "column",
            flex: 1,
          }}
        >
          {showStopDuplicatingPanel ? (
            <div
              style={{
                flex: 1,
                minHeight: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "24px 16px",
                boxSizing: "border-box",
              }}
            >
              <button
                type="button"
                onClick={() => onStopDuplicatingOrUnlockTool()}
                style={{
                  width: "100%",
                  maxWidth: 320,
                  padding: "10px 20px",
                  borderRadius: "6px",
                  border: "1px solid #D1D5DB",
                  fontWeight: 500,
                  fontSize: 14,
                  cursor: "pointer",
                  color: "#374151",
                  backgroundColor: "#F3F4F6",
                  transition: "all 0.2s ease",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                }}
              >
                <svg
                  width={16}
                  height={16}
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 6L18 18M18 6L6 18"
                    stroke="#374151"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Stop duplicating</span>
              </button>
            </div>
          ) : (
            <>
          <div
            className="shape-panel-header"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "25px",
            }}
          >
            <div
              style={{
                fontWeight: 600,
                fontSize: 16,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "70%",
              }}
            >
              {isMultiSelection
                ? "Selection"
                : getDisplayName(effectiveSelectedObject)}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginLeft: "auto",
              }}
            >
              {/* Collapse button */}
              <button
                onClick={() => setCollapsed((prev) => !prev)}
                aria-label={
                  collapsed ? "Expand properties" : "Collapse properties"
                }
                style={{
                  background: "white",
                  border: "none",
                  padding: 4,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg
                  width="14"
                  height="8"
                  viewBox="0 0 14 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{
                    transition: "transform 0.2s ease",
                    transform: collapsed ? "rotate(180deg)" : "rotate(0deg)",
                    transformOrigin: "center",
                  }}
                >
                  <path
                    d="M1.61906 7.75961L0.734375 6.87492L7.36906 0.240234L14.0038 6.87492L13.1191 7.75961L7.36906 2.0168L1.61906 7.75961Z"
                    fill="#6C757D"
                  />
                </svg>
              </button>
            </div>
          </div>
          {!collapsed && (
            <>
              {/* Multi-selection UI - Show when multi-select mode is enabled OR when 2+ elements are selected */}
              {(isMultiSelectMode || isMultiSelection) ? (
                <SelectionEditor
                  selectedObjects={safeSelectedObjects}
                  selectedObject={selectedObject}
                  groupDragDisplay={groupDragDisplay}
                  setSelectedObjects={setSelectedObjects}
                  setSelectedObject={setSelectedObject}
                  deleteSelectedObject={deleteSelectedObject}
                  saveToHistory={saveToHistory}
                  isMultiSelectMode={isMultiSelectMode}
                  setIsMultiSelectMode={setIsMultiSelectMode}
                  individuallySelectedIds={individuallySelectedIds}
                  setIndividuallySelectedIds={setIndividuallySelectedIds}
                  updateShapeProperties={updateShapeProperties}
                  updateArrowProperties={updateArrowProperties}
                  updateDoubleSidedArrowProperties={updateDoubleSidedArrowProperties}
                  updateLineProperties={updateLineProperties}
                  updateTextProperties={updateTextProperties}
                  updateImageProperties={updateImageProperties}
                  handleCopy={handleCopy}
                  handleCut={handleCut}
                  handleCopyGroupBetweenCanvases={handleCopyGroupBetweenCanvases}
                  rectangles={rectangles}
                  circles={circles}
                  images={images}
                  texts={texts}
                  arrows={arrows}
                  lines={lines}
                  doubleSidedArrows={doubleSidedArrows}
                  offCanvasRectangles={offCanvasRectangles}
                  offCanvasCircles={offCanvasCircles}
                  offCanvasImages={offCanvasImages}
                  offCanvasTexts={offCanvasTexts}
                  offCanvasArrows={offCanvasArrows}
                  offCanvasLines={offCanvasLines}
                  offCanvasDoubleSidedArrows={offCanvasDoubleSidedArrows}
                  onToggleCloneMode={onToggleCloneMode}
                  isCloneMode={isCloneMode}
                  setRectangles={setRectangles}
                  setCircles={setCircles}
                  setImages={setImages}
                  setTexts={setTexts}
                  setArrows={setArrows}
                  setLines={setLines}
                  setDoubleSidedArrows={setDoubleSidedArrows}
                  setOffCanvasRectangles={setOffCanvasRectangles}
                  setOffCanvasCircles={setOffCanvasCircles}
                  setOffCanvasImages={setOffCanvasImages}
                  setOffCanvasTexts={setOffCanvasTexts}
                  setOffCanvasArrows={setOffCanvasArrows}
                  setOffCanvasLines={setOffCanvasLines}
                  setOffCanvasDoubleSidedArrows={setOffCanvasDoubleSidedArrows}
                  // Removed state setters for tracking deleted elements
                  setRemovedRectangles={setRemovedRectangles}
                  setRemovedCircles={setRemovedCircles}
                  setRemovedArrows={setRemovedArrows}
                  setRemovedDoubleSidedArrows={setRemovedDoubleSidedArrows}
                  setRemovedScribbles={setRemovedScribbles}
                  setRemovedTexts={setRemovedTexts}
                  setRemovedImages={setRemovedImages}
                  setRemovedVideos={setRemovedVideos}
                  setRemovedLines={setRemovedLines}
                  setRemovedOffCanvasRectangles={setRemovedOffCanvasRectangles}
                  setRemovedOffCanvasCircles={setRemovedOffCanvasCircles}
                  setRemovedOffCanvasImages={setRemovedOffCanvasImages}
                  setRemovedOffCanvasTexts={setRemovedOffCanvasTexts}
                  setRemovedOffCanvasArrows={setRemovedOffCanvasArrows}
                  setRemovedOffCanvasLines={setRemovedOffCanvasLines}
                  setRemovedOffCanvasDoubleSidedArrows={setRemovedOffCanvasDoubleSidedArrows}
                />
              ) : (
                <>
                  {effectiveSelectedObject &&
                    isShapeType(effectiveSelectedObject.type, "Image") && (
                      <ImageEditor
                        selectedImage={effectiveSelectedObjectWithCanvasHint}
                        updateImageProperties={updateImageProperties}
                        toggleImageCompression={toggleImageCompression}
                        onCopy={copySelectedImage}
                        onCut={cutSelectedImage}
                        onDelete={deleteSelectedObject}
                        canvasHeight={canvasHeight}
                        setImages={setImages}
                        setSelectedObject={setSelectedObject}
                        setSelectedObjects={setSelectedObjects}
                        isCropModalOpen={isCropModalOpen}
                        setIsCropModalOpen={setIsCropModalOpen}
                        setCropModalOpenRefOnly={setCropModalOpenRefOnly}
                        setCropModalRootRef={setCropModalRootRef}
                        setIsImageLoading={setIsImageLoading}
                        setImageLoadingProgress={setImageLoadingProgress}
                        saveToHistory={saveToHistory}
                        latestUpdatedImagesRef={latestUpdatedImagesRef}
                        latestImageEditorRefUrlRef={latestImageEditorRefUrlRef}
                        setPendingChanges={setPendingChanges}
                        pendingChangesRef={pendingChangesRef}
                        transformerRef={transformerRef}
                        onToggleCloneMode={onToggleCloneMode}
                        isCloneMode={isCloneMode}
                      />
                    )}

                  {/* Check if it's a unified line/arrow element (has hasStartArrow or hasEndArrow properties) */}
                  {effectiveSelectedObject &&
                    (() => {
                      const isUnifiedElement =
                        (isShapeType(effectiveSelectedObject.type, "Line") ||
                          isShapeType(
                            effectiveSelectedObject.type,
                            "Arrow"
                          ) ||
                          isShapeType(
                            effectiveSelectedObject.type,
                            "DoubleSidedArrow"
                          )) &&
                        (effectiveSelectedObject.hasStartArrow !==
                          undefined ||
                          effectiveSelectedObject.hasEndArrow !== undefined ||
                          effectiveSelectedObject.pointerAtBeginning !==
                            undefined ||
                          effectiveSelectedObject.pointerAtEnd !== undefined);

                      return isUnifiedElement;
                    })() && (
                      <UnifiedLineArrowEditor
                        selectedElement={effectiveSelectedObjectWithCanvasHint}
                        updateElementProperties={updateLineProperties}
                        onDelete={deleteSelectedObject}
                        canvasHeight={canvasHeight}
                        mainCanvasWidthPx={mainCanvasWidthPx}
                        onToggleCloneMode={onToggleCloneMode}
                        isCloneMode={isCloneMode}
                      />
                    )}

                  {/* Show ArrowEditor only for legacy arrows (without hasStartArrow/hasEndArrow properties) */}
                  {effectiveSelectedObject &&
                    (isShapeType(effectiveSelectedObject.type, "Arrow") ||
                      isShapeType(
                        effectiveSelectedObject.type,
                        "DoubleSidedArrow"
                      )) &&
                    effectiveSelectedObject.hasStartArrow === undefined &&
                    effectiveSelectedObject.hasEndArrow === undefined &&
                    effectiveSelectedObject.pointerAtBeginning ===
                      undefined &&
                    effectiveSelectedObject.pointerAtEnd === undefined && (
                      <ArrowEditor
                        selectedArrow={(() => {
                          // Normalize strokeColor to ensure it's always a string
                          const normalizeStrokeColor = (color) => {
                            if (!color) return "#000000";
                            if (
                              typeof color === "string" &&
                              color.startsWith("#")
                            )
                              return color;
                            if (typeof color === "string") return color; // Already a string
                            if (typeof color === "number") {
                              return `#${color
                                .toString(16)
                                .padStart(6, "0")}`;
                            }
                            return "#000000";
                          };

                          // Normalize strokeColor before passing to ArrowEditor
                          const normalizedSelectedObject = {
                            ...effectiveSelectedObject,
                            strokeColor: normalizeStrokeColor(
                              effectiveSelectedObject.strokeColor ||
                                effectiveSelectedObject.stroke
                            ),
                            stroke: normalizeStrokeColor(
                              effectiveSelectedObject.stroke ||
                                effectiveSelectedObject.strokeColor
                            ),
                          };

                          return normalizedSelectedObject;
                        })()}
                        updateArrowProperties={
                          isShapeType(effectiveSelectedObject.type, "Arrow")
                            ? updateArrowProperties
                            : updateDoubleSidedArrowProperties
                        }
                        waypointCount={
                          effectiveSelectedObject.points
                            ? Math.max(
                                0,
                                (effectiveSelectedObject.points.length - 4) /
                                  2
                              )
                            : 0
                        }
                        onDeleteArrow={deleteSelectedObject}
                        onAddText={onAddText}
                        onRemoveText={onRemoveText}
                        canvasHeight={canvasHeight}
                      />
                    )}

                  {effectiveSelectedObject &&
                    isShapeType(effectiveSelectedObject.type, "Rect") &&
                    !effectiveSelectedObject.isTextbox && (
                      <RectangleEditor
                        selectedRectangle={effectiveSelectedObjectWithCanvasHint}
                        updateRectangleProperties={updateShapeProperties}
                        onAddText={onAddText}
                        onRemoveText={onRemoveText}
                        onDelete={deleteSelectedObject}
                        canvasHeight={canvasHeight}
                        mainCanvasWidthPx={mainCanvasWidthPx}
                        onToggleCloneMode={onToggleCloneMode}
                        isCloneMode={isCloneMode}
                      />
                    )}

                  {effectiveSelectedObject &&
                    effectiveSelectedObject.isTextbox &&
                    selectedText && (
                      <TextEditor
                        updateTextProperties={updateTextProperties}
                        selectedText={selectedText}
                        canvasHeight={canvasHeight}
                      />
                    )}

                  {effectiveSelectedObject &&
                    isShapeType(effectiveSelectedObject.type, "Circle") && (
                      <CircleEditor
                        selectedCircle={effectiveSelectedObjectWithCanvasHint}
                        updateCircleProperties={updateShapeProperties}
                        onDelete={deleteSelectedObject}
                        onAddText={onAddText}
                        onRemoveText={onRemoveText}
                        onEditText={() => {}} // Placeholder function - not currently used
                        canvasHeight={canvasHeight}
                        mainCanvasWidthPx={mainCanvasWidthPx}
                        onToggleCloneMode={onToggleCloneMode}
                        isCloneMode={isCloneMode}
                      />
                    )}

                  {/* Show LineEditor only for legacy lines (without hasStartArrow/hasEndArrow properties) */}
                  {effectiveSelectedObject &&
                    isShapeType(effectiveSelectedObject.type, "Line") &&
                    effectiveSelectedObject.hasStartArrow === undefined &&
                    effectiveSelectedObject.hasEndArrow === undefined &&
                    effectiveSelectedObject.pointerAtBeginning ===
                      undefined &&
                    effectiveSelectedObject.pointerAtEnd === undefined && (
                      <LineEditor
                        selectedLine={effectiveSelectedObjectWithCanvasHint}
                        updateLineProperties={updateLineProperties}
                        onDelete={deleteSelectedObject}
                        canvasHeight={canvasHeight}
                      />
                    )}
                </>
              )}
            </>
          )}
            </>
          )}
        </div>
      </div>

      {/* Custom vertical scrollbar (visible only when not collapsed, content is scrollable, and not Arrow/Line) */}
      {!collapsed && !showStopDuplicatingPanel && vScrollable > 0 && (
        <div
          ref={vTrackRef}
          onMouseDown={handleVerticalTrackMouseDown}
          className="custom-scrollbar-track"
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            height: "100%",
            width: 12,
            zIndex: 999,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            background: "transparent",
            cursor: "default",
          }}
        >
          <div
            onMouseDown={handleVerticalThumbMouseDown}
            style={{
              position: "relative",
              top: vThumbTop,
              width: 8,
              height: vThumbHeight,
              borderRadius: 4,
              background: "#9ca3af",
              cursor: "default",
              transition: "background 0.15s ease, opacity 0.15s ease",
              opacity: dragState?.axis === "v" ? 0.8 : 1,
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            }}
          />
        </div>
      )}
    </div>
  </div>
  );
}

ShapeEditor.propTypes = {
  selectedObject: PropTypes.object,
  updateShapeProperties: PropTypes.func.isRequired,
  updateArrowProperties: PropTypes.func.isRequired,
  updateTextProperties: PropTypes.func.isRequired,
  setSelectedObject: PropTypes.func.isRequired,
  selectedText: PropTypes.object,
  deleteSelectedObject: PropTypes.func.isRequired,
  isCloneMode: PropTypes.bool,
  onToggleCloneMode: PropTypes.func,
  lockedAction: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  onStopDuplicatingOrUnlockTool: PropTypes.func,
};

export default ShapeEditor;
